import { jsPDF } from 'jspdf';
import { DEFAULT_PRIMARY } from '@/lib/brand-theme';
import { formatBusinessCategoryLabel } from '@/lib/business-category';
import { sortBusinessDirectoryEntries } from '@/lib/business-directory-params';
import { hslToRgbArr } from '@/lib/hsl-color';
import { formatDisplayPhone, whatsappPhoneHref } from '@/lib/phone-utils';
import { triggerBlobFileDownload } from '@/lib/trigger-download';
import type { BusinessDirectorySort, CommunityBusiness } from '@/types';

const PAGE_MARGIN = 8;
const CARD_GAP_X = 5;
const CARD_GAP_Y = 3.2;
const CARD_PAD_X = 3;
const CARD_PAD_Y = 2.4;
const COLS = 2;
const PHOTO_W = 14;
const PHOTO_H = 16;
const ACTION_SIZE = 4.4;
const CARD_HEIGHT = PHOTO_H + CARD_PAD_Y * 2;
const MAX_PRIMARY_LINES = 1;
const MAX_SECONDARY_LINES = 1;
const MAX_CATEGORY_LINES = 1;
const HEADER_HEIGHT = 22;
const FOOTER_HEIGHT = 12;

function themePrimaryRgb(primary?: string | null): [number, number, number] {
  const hsl = primary?.trim() || DEFAULT_PRIMARY;
  const [r, g, b] = hslToRgbArr(hsl);
  return [Math.round(r * 255), Math.round(g * 255), Math.round(b * 255)];
}

function sanitizeFilenamePart(value: string): string {
  return value
    .replace(/[/\\?%*:|"<>]/g, '')
    .replace(/\s+/g, '_')
    .trim()
    .slice(0, 80);
}

function drawGlobeButton(doc: jsPDF, cx: number, cy: number, size: number, url?: string): void {
  const r = size / 2;
  doc.setFillColor(227, 242, 253);
  doc.circle(cx, cy, r, 'F');
  doc.setDrawColor(21, 101, 192);
  doc.setLineWidth(0.32);
  const ir = r * 0.58;
  doc.circle(cx, cy, ir, 'S');
  doc.ellipse(cx, cy, ir * 0.38, ir, 'S');
  doc.line(cx - ir, cy, cx + ir, cy);
  doc.line(cx - ir * 0.72, cy - ir * 0.42, cx + ir * 0.72, cy - ir * 0.42);
  doc.line(cx - ir * 0.72, cy + ir * 0.42, cx + ir * 0.72, cy + ir * 0.42);

  if (url) {
    doc.link(cx - r, cy - r, size, size, { url });
  }
}

const WHATSAPP_SVG = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 418.135 418.135">
  <circle cx="209.0675" cy="209.0675" r="209.0675" fill="#25D366"/>
  <path fill="#FFFFFF" fill-rule="evenodd" d="M198.929,0.242C88.5,5.5,1.356,97.466,1.691,208.02c0.102,33.672,8.231,65.454,22.571,93.536L2.245,408.429c-1.191,5.781,4.023,10.843,9.766,9.483l104.723-24.811c26.905,13.402,57.125,21.143,89.108,21.631c112.869,1.724,206.982-87.897,210.5-200.724C420.113,93.065,320.295-5.538,198.929,0.242z M323.886,322.197c-30.669,30.669-71.446,47.559-114.818,47.559c-25.396,0-49.71-5.698-72.269-16.935l-14.584-7.265l-64.206,15.212l13.515-65.607l-7.185-14.07c-11.711-22.935-17.649-47.736-17.649-73.713c0-43.373,16.89-84.149,47.559-114.819c30.395-30.395,71.837-47.56,114.822-47.56C252.443,45,293.218,61.89,323.887,92.558c30.669,30.669,47.559,71.445,47.56,114.817C371.446,250.361,354.281,291.803,323.886,322.197z"/>
  <path fill="#FFFFFF" d="M309.712,252.351l-40.169-11.534c-5.281-1.516-10.968-0.018-14.816,3.903l-9.823,10.008c-4.142,4.22-10.427,5.576-15.909,3.358c-19.002-7.69-58.974-43.23-69.182-61.007c-2.945-5.128-2.458-11.539,1.158-16.218l8.576-11.095c3.36-4.347,4.069-10.185,1.847-15.21l-16.9-38.223c-4.048-9.155-15.747-11.82-23.39-5.356c-11.211,9.482-24.513,23.891-26.13,39.854c-2.851,28.144,9.219,63.622,54.862,106.222c52.73,49.215,94.956,55.717,122.449,49.057c15.594-3.777,28.056-18.919,35.921-31.317C323.568,266.34,319.334,255.114,309.712,252.351z"/>
</svg>`;

async function rasterizeSvg(svg: string, size: number): Promise<string | null> {
  if (typeof document === 'undefined') return null;
  try {
    const blob = new Blob([svg], { type: 'image/svg+xml;charset=utf-8' });
    const objectUrl = URL.createObjectURL(blob);
    const img = await new Promise<HTMLImageElement | null>((resolve) => {
      const image = new Image();
      image.onload = () => resolve(image);
      image.onerror = () => resolve(null);
      image.src = objectUrl;
    });
    URL.revokeObjectURL(objectUrl);
    if (!img) return null;

    const canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;
    ctx.drawImage(img, 0, 0, size, size);
    return canvas.toDataURL('image/png');
  } catch {
    return null;
  }
}

let cachedWhatsAppIcon: string | null | undefined;

async function getWhatsAppIconDataUrl(): Promise<string | null> {
  if (cachedWhatsAppIcon !== undefined) return cachedWhatsAppIcon;
  cachedWhatsAppIcon = await rasterizeSvg(WHATSAPP_SVG, 128);
  return cachedWhatsAppIcon;
}

function drawWhatsAppButton(
  doc: jsPDF,
  cx: number,
  cy: number,
  size: number,
  iconDataUrl: string | null,
  url?: string
): void {
  const r = size / 2;
  if (iconDataUrl) {
    try {
      doc.addImage(iconDataUrl, 'PNG', cx - r, cy - r, size, size, undefined, 'FAST');
    } catch {
      doc.setFillColor(37, 211, 102);
      doc.circle(cx, cy, r, 'F');
    }
  } else {
    doc.setFillColor(37, 211, 102);
    doc.circle(cx, cy, r, 'F');
  }
  if (url) {
    doc.link(cx - r, cy - r, size, size, { url });
  }
}

function drawPhotoFallback(doc: jsPDF, x: number, y: number, w: number, h: number): void {
  doc.setFillColor(227, 242, 253);
  doc.roundedRect(x, y, w, h, 1.4, 1.4, 'F');
  doc.setDrawColor(21, 101, 192);
  doc.setLineWidth(0.25);
  const cx = x + w / 2;
  const cy = y + h / 2;
  doc.circle(cx, cy - 1.2, 2.1, 'S');
  doc.ellipse(cx, cy + 3.2, 3.4, 2.2, 'S');
}

function loadImageElement(url: string): Promise<HTMLImageElement | null> {
  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = () => resolve(null);
    img.src = url;
  });
}

async function loadCoverImageDataUrl(url: string | undefined): Promise<string | null> {
  if (!url || typeof document === 'undefined') return null;
  try {
    const img = await loadImageElement(url);
    if (!img) return null;

    const size = 160;
    const canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;

    const scale = Math.max(size / img.width, size / img.height);
    const w = img.width * scale;
    const h = img.height * scale;
    ctx.drawImage(img, (size - w) / 2, (size - h) / 2, w, h);
    return canvas.toDataURL('image/jpeg', 0.9);
  } catch {
    return null;
  }
}

async function loadCircularLogoDataUrl(url: string | undefined): Promise<string | null> {
  if (!url || typeof document === 'undefined') return null;
  try {
    const img = await loadImageElement(url);
    if (!img) return null;

    const size = 160;
    const canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;

    ctx.beginPath();
    ctx.arc(size / 2, size / 2, size / 2, 0, Math.PI * 2);
    ctx.closePath();
    ctx.clip();

    const scale = Math.max(size / img.width, size / img.height);
    const w = img.width * scale;
    const h = img.height * scale;
    ctx.drawImage(img, (size - w) / 2, (size - h) / 2, w, h);
    return canvas.toDataURL('image/png');
  } catch {
    return null;
  }
}

function drawPageBase(doc: jsPDF, pageWidth: number, pageHeight: number): void {
  doc.setFillColor(255, 255, 255);
  doc.rect(0, 0, pageWidth, pageHeight, 'F');
}

function truncateLines(doc: jsPDF, text: string, maxWidth: number, maxLines: number): string[] {
  const lines = doc.splitTextToSize(text, maxWidth) as string[];
  if (lines.length <= maxLines) return lines;

  const kept = lines.slice(0, maxLines);
  const last = kept[maxLines - 1] ?? '';
  const ellipsis = '…';
  let clipped = last;
  while (clipped.length > 0 && doc.getTextWidth(clipped + ellipsis) > maxWidth) {
    clipped = clipped.slice(0, -1);
  }
  kept[maxLines - 1] = `${clipped.trimEnd()}${ellipsis}`;
  return kept;
}

function cardTitleLines(
  business: CommunityBusiness,
  sort: BusinessDirectorySort
): { primary: string; secondary: string | null } {
  const owner = business.owner.name?.trim() || '';
  const businessName = business.name.trim();

  if (sort === 'business') {
    return {
      primary: businessName || owner,
      secondary: businessName && owner && owner !== businessName ? owner : null,
    };
  }

  return {
    primary: owner || businessName,
    secondary: owner && businessName && owner !== businessName ? businessName : null,
  };
}

function drawBusinessCard(
  doc: jsPDF,
  business: CommunityBusiness,
  x: number,
  y: number,
  cardWidth: number,
  photoDataUrl: string | null,
  whatsappIconDataUrl: string | null,
  sort: BusinessDirectorySort
): void {
  const cardHeight = CARD_HEIGHT;

  doc.setFillColor(180, 180, 180);
  doc.roundedRect(x + 0.55, y + 0.7, cardWidth, cardHeight, 2.4, 2.4, 'F');

  doc.setFillColor(255, 255, 255);
  doc.setDrawColor(40, 40, 40);
  doc.setLineWidth(0.35);
  doc.roundedRect(x, y, cardWidth, cardHeight, 2.4, 2.4, 'FD');

  const photoX = x + CARD_PAD_X;
  const photoY = y + CARD_PAD_Y;
  if (photoDataUrl) {
    try {
      doc.addImage(photoDataUrl, 'JPEG', photoX, photoY, PHOTO_W, PHOTO_H, undefined, 'FAST');
      doc.setDrawColor(40, 40, 40);
      doc.setLineWidth(0.2);
      doc.roundedRect(photoX, photoY, PHOTO_W, PHOTO_H, 1.2, 1.2, 'S');
    } catch {
      drawPhotoFallback(doc, photoX, photoY, PHOTO_W, PHOTO_H);
    }
  } else {
    drawPhotoFallback(doc, photoX, photoY, PHOTO_W, PHOTO_H);
  }

  const textX = photoX + PHOTO_W + 2.4;
  const textWidth = cardWidth - CARD_PAD_X * 2 - PHOTO_W - 2.5;
  let cursorY = y + CARD_PAD_Y + 3;

  const titles = cardTitleLines(business, sort);

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.setTextColor(20, 20, 20);
  const nameLines = truncateLines(doc, titles.primary, textWidth, MAX_PRIMARY_LINES);
  doc.text(nameLines, textX, cursorY);
  cursorY += nameLines.length * 3.4;

  if (titles.secondary) {
    doc.setFont('helvetica', 'italic');
    doc.setFontSize(8);
    doc.setTextColor(35, 35, 35);
    const secondaryLines = truncateLines(doc, titles.secondary, textWidth, MAX_SECONDARY_LINES);
    doc.text(secondaryLines, textX, cursorY);
    cursorY += secondaryLines.length * 3.1;
  }

  const categoryLabel = formatBusinessCategoryLabel(
    business.category,
    business.subCategory,
    business.selfDefinedCategory
  );
  if (categoryLabel) {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7.5);
    doc.setTextColor(40, 40, 40);
    const lines = truncateLines(doc, categoryLabel, textWidth, MAX_CATEGORY_LINES);
    doc.text(lines, textX, cursorY);
  }

  const whatsappHref = whatsappPhoneHref(business.phone);
  const rowY = y + CARD_PAD_Y + PHOTO_H - 1.2;
  let iconX = textX + ACTION_SIZE / 2;

  if (whatsappHref) {
    drawWhatsAppButton(doc, iconX, rowY, ACTION_SIZE, whatsappIconDataUrl, whatsappHref);
    iconX += ACTION_SIZE + 1.6;
  }

  if (business.website) {
    drawGlobeButton(doc, iconX, rowY, ACTION_SIZE, business.website);
    iconX += ACTION_SIZE + 1.8;
  }

  if (business.phone) {
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8);
    doc.setTextColor(21, 101, 192);
    const phoneLabel = formatDisplayPhone(business.phone);
    const phoneMaxWidth = x + cardWidth - CARD_PAD_X - iconX;
    const phoneLines = truncateLines(doc, phoneLabel, phoneMaxWidth, 1);
    doc.text(phoneLines, iconX, rowY + 1.1);
    const tel = business.phone.startsWith('+') ? business.phone : `+${business.phone}`;
    doc.link(iconX, rowY - 2.2, Math.min(28, phoneMaxWidth), 4.4, { url: `tel:${tel}` });
  }
}

function drawPageHeader(
  doc: jsPDF,
  communityName: string,
  pageWidth: number,
  communityLogoDataUrl: string | null,
  themeRgb: [number, number, number]
): number {
  doc.setFillColor(...themeRgb);
  doc.rect(0, 0, pageWidth, HEADER_HEIGHT, 'F');

  let titleLeft = PAGE_MARGIN;

  if (communityLogoDataUrl) {
    const logoSize = 14;
    const logoY = (HEADER_HEIGHT - logoSize) / 2;
    const logoX = PAGE_MARGIN;
    try {
      doc.setFillColor(255, 255, 255);
      doc.circle(logoX + logoSize / 2, logoY + logoSize / 2, logoSize / 2 + 0.6, 'F');
      doc.addImage(
        communityLogoDataUrl,
        'PNG',
        logoX,
        logoY,
        logoSize,
        logoSize,
        undefined,
        'FAST'
      );
      titleLeft = logoX + logoSize + 4;
    } catch {
      titleLeft = PAGE_MARGIN;
    }
  }

  const titleWidth = Math.max(40, pageWidth - PAGE_MARGIN - titleLeft);

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(18);
  doc.setTextColor(255, 255, 255);
  const headerLines = doc.splitTextToSize(communityName, titleWidth) as string[];
  const titleBlockHeight = headerLines.length * 7;
  const titleY = HEADER_HEIGHT / 2 - titleBlockHeight / 2 + 5;
  doc.text(headerLines, titleLeft, titleY);

  return HEADER_HEIGHT + 5;
}

function drawPageFooter(
  doc: jsPDF,
  pageWidth: number,
  pageHeight: number,
  page: number,
  total: number,
  themeRgb: [number, number, number]
): void {
  doc.setFillColor(...themeRgb);
  doc.rect(0, pageHeight - FOOTER_HEIGHT, pageWidth, FOOTER_HEIGHT, 'F');
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(12);
  doc.setTextColor(255, 255, 255);
  doc.text(`${page}/${total}`, pageWidth - PAGE_MARGIN, pageHeight - 4.2, { align: 'right' });
}

export async function downloadBusinessDirectoryPdf(
  businesses: CommunityBusiness[],
  communityName: string,
  communityLogoUrl?: string | null,
  sort: BusinessDirectorySort = 'business',
  themePrimary?: string | null
): Promise<void> {
  const orderedBusinesses = sortBusinessDirectoryEntries(businesses, sort);
  const themeRgb = themePrimaryRgb(themePrimary);
  const doc = new jsPDF({ unit: 'mm', format: 'a4' });
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const contentWidth = pageWidth - PAGE_MARGIN * 2;
  const cardWidth = (contentWidth - CARD_GAP_X * (COLS - 1)) / COLS;
  const contentBottom = pageHeight - FOOTER_HEIGHT - 4;

  const [photos, whatsappIconDataUrl, communityLogoDataUrl] = await Promise.all([
    Promise.all(orderedBusinesses.map((b) => loadCoverImageDataUrl(b.logo))),
    getWhatsAppIconDataUrl(),
    loadCircularLogoDataUrl(communityLogoUrl ?? undefined),
  ]);

  const startPage = () => {
    drawPageBase(doc, pageWidth, pageHeight);
    return drawPageHeader(doc, communityName, pageWidth, communityLogoDataUrl, themeRgb);
  };

  let y = startPage();

  if (orderedBusinesses.length === 0) {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(11);
    doc.setTextColor(80, 80, 80);
    doc.text('No businesses to export.', pageWidth / 2, y + 10, { align: 'center' });
  } else {
    for (let i = 0; i < orderedBusinesses.length; i += COLS) {
      const row = orderedBusinesses.slice(i, i + COLS);

      if (y + CARD_HEIGHT > contentBottom) {
        doc.addPage();
        y = startPage();
      }

      row.forEach((business, col) => {
        const x = PAGE_MARGIN + col * (cardWidth + CARD_GAP_X);
        drawBusinessCard(
          doc,
          business,
          x,
          y,
          cardWidth,
          photos[i + col] ?? null,
          whatsappIconDataUrl,
          sort
        );
      });

      y += CARD_HEIGHT + CARD_GAP_Y;
    }
  }

  const totalPages = doc.getNumberOfPages();
  for (let page = 1; page <= totalPages; page += 1) {
    doc.setPage(page);
    drawPageFooter(doc, pageWidth, pageHeight, page, totalPages, themeRgb);
  }

  const filename = `${sanitizeFilenamePart(communityName) || 'community'}_business_directory.pdf`;
  triggerBlobFileDownload(doc.output('blob'), filename);
}
